import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { MsContact } from '../../messages/types';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';

export interface MessagesComposeSendPayload {
  phone: string;
  text: string;
  contact: MsContact | null;
}

export interface MessagesComposeProps {
  onClose: () => void;
  minimized: boolean;
  onMinimize: () => void;
  onRestore: () => void;
  contacts?: MsContact[];
  onRecipientQueryChange?: (query: string) => void;
  onSend?: (payload: MessagesComposeSendPayload) => void;
  sending?: boolean;
}

function normalizePhoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

function isValidPhoneInput(value: string): boolean {
  return normalizePhoneDigits(value).length >= 10;
}

function resolveComposeTarget(
  recipient: MsContact | null,
  input: string,
): { phone: string; contact: MsContact | null } | null {
  if (recipient?.phone.trim()) {
    return { phone: recipient.phone.trim(), contact: recipient };
  }
  const trimmed = input.trim();
  if (isValidPhoneInput(trimmed)) {
    return { phone: trimmed, contact: null };
  }
  return null;
}

export function MessagesCompose({
  onClose,
  minimized,
  onMinimize,
  onRestore,
  contacts = [],
  onRecipientQueryChange,
  onSend,
  sending = false,
}: MessagesComposeProps) {
  const [recipient, setRecipient] = useState<MsContact | null>(null);
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const ddRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ddRef.current?.contains(t) || inputRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
    };
  }, []);

  const filteredContacts = useMemo(() => {
    if (!input) return contacts;
    const q = input.toLowerCase();
    return contacts.filter(
      (c) => c.name.toLowerCase().includes(q) || c.phone.includes(q),
    );
  }, [contacts, input]);

  const target = resolveComposeTarget(recipient, input);
  const canSend = Boolean(target && text.trim().length > 0 && !sending);
  const willTranslate = recipient?.lang === 'Spanish';
  const isUnicode = /[^\x00-\x7F]/.test(text);
  const limit = isUnicode ? 70 : 160;
  const parts = text.length === 0 ? 0 : Math.ceil(text.length / limit);

  if (minimized) {
    return (
      <div className="ms-compose-min" onClick={onRestore} role="button" tabIndex={0}>
        <Icon kind="chat" size={14} />
        <span className="flex-1 truncate text-[13px] font-semibold">
          {recipient?.name ?? target?.phone ?? 'New message'}
        </span>
        <button
          type="button"
          className="inline-flex border-0 bg-transparent text-white"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <Icon kind="x" size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className="ms-compose">
      <div
        className="flex items-center gap-[9px] bg-[var(--sage-0)] px-[15px] py-[11px] text-white"
        style={{ borderRadius: 'var(--r-lg) var(--r-lg) 0 0' }}
      >
        <Icon kind="chat" size={15} />
        <span className="flex-1 text-sm font-semibold">New message</span>
        <button type="button" className="inline-flex border-0 bg-transparent p-[3px] text-white" onClick={onMinimize} title="Minimize">
          <Icon kind="minus" size={15} />
        </button>
        <button type="button" className="inline-flex border-0 bg-transparent p-[3px] text-white" onClick={onClose} title="Close">
          <Icon kind="x" size={15} />
        </button>
      </div>

      <div className="relative border-b border-[var(--cream-2)] px-[15px] py-[11px]">
        <div className="flex items-center gap-2">
          <span className="w-[30px] shrink-0 text-[12.5px] font-semibold text-[var(--ink-3)]" style={{ flex: 'none' }}>
            To
          </span>
          <div className="flex min-h-7 min-w-0 flex-1 flex-wrap items-center gap-[5px]">
            {recipient ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-[var(--r-sm)] bg-[var(--gold-tint)] text-[12.5px] font-semibold text-[var(--gold)]"
                style={{ padding: '3px 5px 3px 10px' }}
              >
                {recipient.name}
                <span className="font-[var(--font-mono)] text-[11px] opacity-70">{recipient.phone}</span>
                <button
                  type="button"
                  className="inline-flex border-0 bg-transparent text-[var(--gold)]"
                  onClick={() => {
                    setRecipient(null);
                  }}
                >
                  <Icon kind="x" size={12} />
                </button>
              </span>
            ) : (
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  const next = e.target.value;
                  setInput(next);
                  onRecipientQueryChange?.(next);
                  setOpen(true);
                }}
                onFocus={() => {
                  setOpen(true);
                }}
                placeholder="Search patients or type a number…"
                className="min-w-[140px] flex-1 border-0 bg-transparent font-[var(--font-body)] text-[13.5px] text-[var(--ink-1)] outline-none"
              />
            )}
          </div>
          <IconButton
            icon="book"
            size={28}
            label="Address book"
            onClick={() => {
              setOpen(true);
            }}
          />
        </div>
        {open && !recipient ? (
          <div ref={ddRef} className="ms-addr">
            <div className="px-2 pb-2 pt-1.5 font-[var(--font-mono)] text-[9px] font-bold uppercase tracking-[0.12em] text-[var(--ink-4)]">
              Patients · {filteredContacts.length}
            </div>
            {filteredContacts.slice(0, 6).map((c) => (
              <div
                key={c.id}
                className="ms-addr-row"
                onClick={() => {
                  setRecipient(c);
                  setOpen(false);
                  setInput('');
                }}
              >
                <span className="ms-av ms-av-patient h-[30px] w-[30px] text-[11px]">{c.initials}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-[var(--ink-0)]">{c.name}</div>
                  <div className="mt-px font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">{c.phone}</div>
                </div>
                {c.lang === 'Spanish' ? (
                  <span className="rounded bg-[var(--ai-tint)] px-[5px] py-px text-[9px] font-bold text-[var(--ai-3)]">
                    ES
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {willTranslate ? (
        <div className="flex items-center gap-[7px] border-b border-[color-mix(in_srgb,var(--ai-3)_20%,transparent)] bg-[var(--ai-tint)] px-[15px] py-2 text-xs text-[var(--ai-3)]">
          <Icon kind="translate" size={12} />
          Writing in <b>English</b> — sends to patient in <b>Spanish</b>.
        </div>
      ) : null}

      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
        }}
        placeholder="Write your message…"
        className="min-h-[140px] flex-1 resize-none border-0 bg-transparent px-[15px] py-[15px] font-[var(--font-body)] text-sm leading-[1.6] text-[var(--ink-1)] outline-none"
      />

      <div className="flex items-center gap-2 border-t border-[var(--cream-2)] bg-[var(--cream-0)] px-[15px] py-[11px]">
        <Button
          variant="primary"
          size="sm"
          icon="send"
          disabled={!canSend}
          onClick={() => {
            if (!target || !text.trim()) return;
            onSend?.({ phone: target.phone, text: text.trim(), contact: target.contact });
          }}
        >
          Send
        </Button>
        <IconButton icon="attach" size={30} label="Attach" />
        {text.length > 0 ? (
          <span
            className="ml-auto text-[11.5px]"
            style={{ color: parts > 1 ? 'var(--gold)' : 'var(--ink-4)' }}
          >
            <span className="font-[var(--font-mono)]">
              {text.length}/{limit}
            </span>
            {parts > 1 ? (
              <span className="font-semibold">
                {' '}
                · {parts} parts
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
  );
}
